import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import SuperAdminSidebar from '../components/SuperAdminSidebar';
import AdminTopBar from '../components/AdminTopBar';
import { API_ENDPOINTS } from '../config/api';
import { useInstitutionalResources } from '../contexts/InstitutionalDataContext';

const InstitutionalEvaluationTemplateResultsPage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  // Programs come from the shared cache. No need to re-fetch when navigating
  // here from the evaluations page.
  const { entries: instEntries } = useInstitutionalResources(['programs']);
  const programs = instEntries.programs?.data || [];
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const breadcrumbItems = [
    { href: '/institutional-admin/dashboard', label: 'Dashboard' },
    { href: '/institutional-admin/evaluations', label: 'Evaluations' },
    { label: 'Completed Eval Details' },
  ];

  useEffect(() => {
    const role = localStorage.getItem('role');
    const token = localStorage.getItem('token');
    if (!token || role !== 'institutional_admin') {
      window.location.replace('/');
      return;
    }

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(
          `${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/evaluation-templates/${templateId}/results`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to load completed evaluation details');
        const data = await res.json();
        setResults(data);
      } catch (e) {
        setError(e.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [templateId]);

  const filteredAssignments = useMemo(() => {
    const rows = results?.assignments || [];
    const q = search.trim().toLowerCase();
    return rows.filter((a) => {
      const assignee = `${a.assignee?.firstName || ''} ${a.assignee?.lastName || ''}`.trim().toLowerCase();
      const assignedTo = `${a.assignedTo?.firstName || ''} ${a.assignedTo?.lastName || ''}`.trim().toLowerCase();
      const email = (a.assignee?.email || '').toLowerCase();
      const matchesSearch = !q || assignee.includes(q) || assignedTo.includes(q) || email.includes(q);
      const matchesRole = roleFilter === 'all' || (a.assignee?.role || '').toLowerCase() === roleFilter;
      const matchesProgram = programFilter === 'all' || String(a.assignee?.programId || '') === programFilter;
      const matchesStatus = statusFilter === 'all' || (a.status || '').toLowerCase() === statusFilter;
      return matchesSearch && matchesRole && matchesProgram && matchesStatus;
    });
  }, [results, search, roleFilter, programFilter, statusFilter]);

  return (
    <div className="d-flex vh-100 overflow-hidden">
      <SuperAdminSidebar variant="institutional" />
      <div className="d-flex flex-column flex-grow-1 min-w-0 overflow-auto">
        <AdminTopBar variant="institutional" breadcrumbItems={breadcrumbItems} />
        <Container fluid className="bg-inverse-light">
          {error && <Alert variant="danger" className="mt-2">{error}</Alert>}
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <Row className="g-3">
              <Col xl={2} lg={3} md={3} sm={4} className="border-end bg-inverse-light pt-3 adjust-min-vh">
                <div className="px-2">
                  <h6 className="mb-1">{results?.template?.title || 'Completed Evaluation'}</h6>
                  <div className="text-muted small mb-3">
                    {results?.template?.evaluationType || 'survey'}
                  </div>
                  <div className="d-grid gap-2">
                    <Card className="border-0 shadow-sm">
                      <Card.Body className="py-2">
                        <small className="text-muted d-block">Assigned</small>
                        <div className="fw-semibold">{results?.summary?.assignedCount ?? 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="border-0 shadow-sm">
                      <Card.Body className="py-2">
                        <small className="text-muted d-block">Completed</small>
                        <div className="fw-semibold">{results?.summary?.completedCount ?? 0}</div>
                      </Card.Body>
                    </Card>
                    <Card className="border-0 shadow-sm">
                      <Card.Body className="py-2">
                        <small className="text-muted d-block">Pending</small>
                        <div className="fw-semibold">{results?.summary?.pendingCount ?? 0}</div>
                      </Card.Body>
                    </Card>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="mt-3 w-100"
                    onClick={() => navigate('/institutional-admin/evaluations')}
                  >
                    Back to Evaluations
                  </Button>
                </div>
              </Col>

              <Col xl={10} lg={9} md={9} sm={8} className="adjust-min-vh p-0">
                <Card className="p-0 border-0 rounded-0 h-100">
                  <Card.Header className="d-flex px-4 py-3 justify-content-between align-items-center custom-sticky-top border-bottom-0 gap-2 flex-wrap">
                    <div className="d-flex flex-column">
                      <h6 className="mb-0">Submitted/Assigned Users</h6>
                      <div className="text-muted small">Search and filter evaluation submissions.</div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
                      <Form.Control
                        size="sm"
                        placeholder="Search user, email, assigned-to..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: 250 }}
                      />
                      <Form.Select size="sm" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} style={{ width: 130 }}>
                        <option value="all">All roles</option>
                        <option value="resident">Resident</option>
                        <option value="attending">Attending</option>
                        <option value="director">Director</option>
                      </Form.Select>
                      <Form.Select size="sm" value={programFilter} onChange={(e) => setProgramFilter(e.target.value)} style={{ width: 170 }}>
                        <option value="all">All programs</option>
                        {programs.map((p) => (
                          <option key={p.id} value={String(p.id)}>{p.name}</option>
                        ))}
                      </Form.Select>
                      <Form.Select size="sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 130 }}>
                        <option value="all">All statuses</option>
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                      </Form.Select>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive alt-table-striped users-table-scroll-wrapper">
                      <table className="table table-hover table-header-sticky mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>User</th>
                            <th>Role</th>
                            <th>Assigned To</th>
                            <th>Status</th>
                            <th>Completed At</th>
                            <th className="text-end">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAssignments.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="text-center text-muted py-4">No rows match the selected filters.</td>
                            </tr>
                          ) : (
                            filteredAssignments.map((a) => (
                              <tr key={a.assignmentId}>
                                <td>{a.assignee ? `${a.assignee.firstName} ${a.assignee.lastName}` : 'Unknown'}</td>
                                <td>{a.assignee?.role || '—'}</td>
                                <td>{a.assignedTo ? `${a.assignedTo.firstName} ${a.assignedTo.lastName}` : 'Self'}</td>
                                <td>
                                  <Badge bg={a.status === 'completed' ? 'success' : 'secondary'}>{a.status}</Badge>
                                </td>
                                <td>{a.completedAt ? new Date(a.completedAt).toLocaleString() : '—'}</td>
                                <td className="text-end">
                                  {a.evaluation?.responseId ? (
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() =>
                                        navigate(`/institutional-admin/evaluations/responses/${a.evaluation.responseId}`)
                                      }
                                    >
                                      View Result
                                    </Button>
                                  ) : (
                                    <span className="text-muted small">Not submitted</span>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </div>
  );
};

export default InstitutionalEvaluationTemplateResultsPage;
